import { Button } from '@/components/ui/button'
import { Stepper, type Step } from '@/components/ui/stepper'
import {
  AgreementSchema,
  createAbonosAgreementSchema,
  CreateAgreementData,
  createBaseAgreementSchema,
  createCremacionesAgreementSchema,
} from '@/domain/agreements/agreement'
import { AssuredPersonRequest } from '@/domain/assured/Assured'
import { CollectorStatus } from '@/domain/collectors/collector'
import { DeathData } from '@/domain/deathData/deathData'
import { formatBillingFrequencyOnLabel, Offering, ServiceType } from '@/domain/offerings/offering'
import { formatSellCode, SellCode } from '@/domain/parcels/parcel'
import { useCreateAgreement, useLatestContractNumber } from '@/hooks/api/useAgreements'
import { useClient } from '@/hooks/api/useClients'
import { useCollectors } from '@/hooks/api/useCollectors'
import { useOfferingAgePricing } from '@/hooks/api/useOfferingAgePricing'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useSellers } from '@/hooks/api/useSellers'
import { Paths } from '@/routes/paths'
import { convertToDigitStr } from '@/utils/helpers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { addMonths, differenceInYears } from 'date-fns'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { renderSinglePeriod } from '../../shared/utils'
import { AgreementDetailsForm } from './AgreementDetailsForm'
import { AgreementSummary } from './AgreementSummary'
import { AssuredPeopleDataForm } from './AssuredPeopleDataForm'
import { ClientServiceSelector } from './ClientServiceSelector'
import { DeathDataForm } from './DeathDataForm'
import { StepHeader } from './StepHeader'
import { Card, CardContent } from '@/components/ui/card'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateAgreementStep = 'service-selection' | 'death-data' | 'summary'

interface AgreementFormInnerProps {
  /** The offering the user selected */
  offering: Offering
  /** The pre-resolved Zod schema for the current offering combination */
  schema: AgreementSchema
  /** The pre-selected userId (already picked in step 0) */
  initialUserId: string
  onUserIdChange: (id: string) => void
  onOfferingIdChange: (id: string) => void
  onServiceTypeChange: (type: ServiceType | undefined) => void
}

// ---------------------------------------------------------------------------
// Inner form component – re-mounted whenever offering / hasPaymentPlan changes
// This guarantees react-hook-form always gets the correct resolver.
// ---------------------------------------------------------------------------

const AgreementFormInner = ({ offering, schema, initialUserId, onUserIdChange, onOfferingIdChange, onServiceTypeChange }: AgreementFormInnerProps) => {
  const [step, setStep] = useState<CreateAgreementStep>('service-selection')
  const [deathData, setDeathData] = useState<DeathData[]>([])
  const [assuredPeople, setAssuredPeople] = useState<AssuredPersonRequest[]>([])
  const navigate = useNavigate()
  const createMutation = useCreateAgreement()

  const { data: collectors = [] } = useCollectors({ status: CollectorStatus.Active })
  const { data: sellers = [] } = useSellers({ status: CollectorStatus.Active })

  const form = useForm<CreateAgreementData>({
    resolver: zodResolver(schema as any),
    defaultValues: {
      userId: initialUserId,
      offeringId: offering.id,
      serviceType: offering.serviceType,
      notes: '',
      startDate: new Date(),
      endDate: undefined,
      amount: offering.basePrice ?? 0,
      initialAmount: 0,
      hasPaymentPlan: offering.hasPaymentPlanAvailable,
      installmentAmount: undefined,
      installments: undefined,
      sellCode: SellCode.PRENECESSITY,
      contractNumber: undefined,
      sellerId: undefined,
      collectorId: undefined
    },
  })

  // console.log(form.getValues())

  // Fetch latest contract number for cremations
  const { data: latestContractNumber, isLoading: isLoadingContractNumber } = useLatestContractNumber(
    offering.serviceType === ServiceType.CREMACIONES ? offering.serviceType : ServiceType.ABONOS
  )

  useEffect(() => {
    if (latestContractNumber) {
      const match = latestContractNumber.match(/^(C-|A-)(\d+)$/);
      if (match) {
        const nextNumber = parseInt(match[2], 10) + 1;
        form.setValue('contractNumber', `${match[1]}${String(nextNumber).padStart(match[2].length, '0')}`);
      } else {
        form.setValue('contractNumber', latestContractNumber!);
      }
    }
  }, [latestContractNumber])


  const userId = form.watch('userId')
  const offeringId = form.watch('offeringId')
  const { data: agePricing } = useOfferingAgePricing(offeringId)
  const amount = form.watch('amount')
  const installmentAmount = form.watch('installmentAmount')
  const initialAmount = form.watch('initialAmount')
  const installments = form.watch('installments') || undefined
  const installmentsValue = installmentAmount && installments ? installmentAmount / installments : undefined
  const startDate = form.watch('startDate')
  const hasPaymentPlan = form.watch('hasPaymentPlan') ?? false
  const serviceType = offering.serviceType

  const { data: client } = useClient(userId)
  const sellerId = form.watch('sellerId')
  const collectorId = form.watch('collectorId')
  const seller = sellers?.find((s) => s.id === sellerId)
  const collector = collectors?.find((c) => c.id === collectorId)

  // Steps -----------------------------------------------------------------

  const stepperSteps: Step[] = [
    { id: 'service-selection', label: 'Selección', description: 'Cliente y Servicio' },
    { id: 'death-data', label: serviceType === ServiceType.ABONOS ? 'Datos del Abonado' : 'Datos del Fallecido', description: 'Información requerida' },
    { id: 'summary', label: 'Resumen', description: 'Confirmar contratación' },
  ]

  const getFilteredSteps = (): Step[] => {
    if (!serviceType || !['abonos', 'cremaciones'].includes(serviceType)) {
      return stepperSteps.filter((s) => s.id !== 'death-data')
    }
    return stepperSteps
  }

  // Propagate changes upward if user changes client or offering
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.userId !== undefined && values.userId !== initialUserId) onUserIdChange(values.userId)
      if (values.offeringId !== undefined && values.offeringId !== offering.id) onOfferingIdChange(values.offeringId)
      if (values.serviceType !== undefined && values.serviceType !== offering.serviceType) onServiceTypeChange(values.serviceType)
    })
    return () => subscription.unsubscribe()
  }, [form, initialUserId, offering.id, offering.serviceType, onUserIdChange, onOfferingIdChange, onServiceTypeChange])

  // Auto-fill notes -------------------------------------------------------

  const buildNoteMsgAbonos = () => {
    const clientName = client?.userData.legalName
    let str = `Contratación generada para el cliente ${clientName} utilizando el servicio ${offering.name} por un monto inicial ${formatBillingFrequencyOnLabel(offering.billingFrequency)} de $${convertToDigitStr(amount)}.`
    if (hasPaymentPlan) {
      str += ` Período de inicio del abono: ${renderSinglePeriod(addMonths(startDate, 1), '/')}.`
      if (initialAmount && initialAmount > 0) {
        str += ` Se cobrará una entrega inicial de $${convertToDigitStr(initialAmount)} y un monto de anticipo de $${convertToDigitStr(installmentAmount)} en ${installments ?? 0} cuotas de $${convertToDigitStr(installmentsValue)}.`
      } else {
        str += ` Se cobrará un anticipo de $${convertToDigitStr(installmentAmount)} en ${installments ?? 0} cuotas de $${convertToDigitStr(installmentsValue)}.`
      }
    } else {
      str += ` Período de inicio del abono: ${renderSinglePeriod(startDate, '/')}.`
    }
    return str
  }

  useEffect(() => {
    if (client && offering) {
      const clientName = client.userData.legalName
      if (serviceType === ServiceType.CREMACIONES) {
        const sellCode = form.watch('sellCode')
        if (sellCode) {
          form.setValue('notes', `Contratación generada para el cliente ${clientName} utilizando el servicio ${offering.name} (${formatSellCode(sellCode)}) por un monto final de $${convertToDigitStr(amount)}`)
        }
      } else if (serviceType === ServiceType.ABONOS) {
        form.setValue('notes', buildNoteMsgAbonos())
        form.setValue('sellCode', undefined)
      }
    }
  }, [client, offering, amount, hasPaymentPlan, installmentAmount, initialAmount, startDate, installments, form.watch('sellCode')])

  // Navigation ------------------------------------------------------------

  const handleSubmit = async () => {
    if (step === 'service-selection') {
      const isValid = await form.trigger([
        'userId', 'offeringId', 'startDate', 'endDate', 'amount',
        'hasPaymentPlan', 'initialAmount', 'installmentAmount', 'installments', 'sellCode', 'contractNumber'
      ])
      if (isValid) {
        setStep('death-data')
      }
    } else if (step === 'death-data') {
      setStep('summary')
    }
  }

  const handleDeathDataSubmit = (data: DeathData[]) => {
    setDeathData(data.sort((a, b) => b.birthDate && a.birthDate ? a.birthDate.getTime() - b.birthDate.getTime() : 0))
    setStep('summary')
  }

  const handleAssuredPeopleSubmit = (data: AssuredPersonRequest[]) => {
    setAssuredPeople(data)
    if (offering.hasAgePricing && agePricing && agePricing.ranges.length > 0) {
      const oldestAge = data.reduce((max, person) => {
        if (!person.dateOfBirth) return max
        return Math.max(max, differenceInYears(new Date(), person.dateOfBirth))
      }, 0)
      const foundRange = agePricing.ranges.find(
        (range) => oldestAge >= range.rangeStart && oldestAge <= range.rangeEnd
      )
      if (foundRange) form.setValue('amount', foundRange.price)
    }
    setStep('summary')
  }

  const handleBack = () => {
    if (step === 'death-data') {
      setStep('service-selection')
    } else if (step === 'summary') {
      if (serviceType && ['abonos', 'cremaciones'].includes(serviceType)) {
        setStep('death-data')
      } else {
        setStep('service-selection')
      }
    }
  }

  // console.log(form.formState.errors)

  const onSubmit = async (data: CreateAgreementData) => {
    try {
      const payload = {
        ...data,
        deathData: deathData.length > 0 ? deathData : undefined,
        assuredPeople: assuredPeople.length > 0 ? assuredPeople : undefined,
      }

      const response = await createMutation.mutateAsync(payload)
      if (response.billingPeriodId) {
        navigate({ to: `${Paths.Management.SubscriptionPeriodPayments}/${response.billingPeriodId}/${Paths.Management.Payments}/${response.payment.id}` })
      } else {
        navigate({ to: `${Paths.Management.Contracts}/${response.agreement.id}/${Paths.Management.Payments}/${response.payment.id}` })
      }
      form.reset()
      setDeathData([])
      setAssuredPeople([])
      setStep('service-selection')
    } catch (error) {
      console.error(error)
    }
  }

  // Render steps ----------------------------------------------------------

  const renderServiceSelectionStep = () => (
    <div className="space-y-6">
      <StepHeader title="Seleccione el cliente y servicio a prestar" description="">
        <ClientServiceSelector form={form} />
      </StepHeader>

      <AgreementDetailsForm form={form} serviceType={serviceType!} offering={offering} collectors={collectors} sellers={sellers} />

      <div className="flex justify-between space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={() => navigate({ to: `${Paths.Management.Contracts}` })}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleSubmit}>
          Siguiente
        </Button>
      </div>
    </div>
  )

  const renderDeathDataStep = () => (
    <div className="space-y-6">
      <StepHeader title="">
        {serviceType === ServiceType.ABONOS ? (
          <AssuredPeopleDataForm
            offering={offering}
            onSubmit={handleAssuredPeopleSubmit}
            onBack={handleBack}
            existingData={assuredPeople}
            userId={userId}
          />
        ) : (
          <DeathDataForm
            offering={offering}
            onSubmit={handleDeathDataSubmit}
            onBack={handleBack}
            existingData={deathData}
          />
        )}
      </StepHeader>
    </div>
  )

  const renderSummaryStep = () => (
    <div className="space-y-6">
      <AgreementSummary
        form={form}
        agreement={form.getValues()}
        client={client}
        seller={seller}
        offering={offering}
        deathData={deathData}
        assuredPeople={assuredPeople}
        ranges={agePricing?.ranges}
        collector={collector}
      />

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={() => setStep('death-data')} disabled={createMutation.isPending}>
          Atrás
        </Button>
        <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={createMutation.isPending}>
          Confirmar Contratación
        </Button>
      </div>
    </div>
  )

  return (
    <Card className='p-6'>
      <div className="mb-8">
        <Stepper steps={getFilteredSteps()} currentStep={step} />
      </div>
      <CardContent>
        {step === 'service-selection' && renderServiceSelectionStep()}
        {step === 'death-data' && renderDeathDataStep()}
        {step === 'summary' && renderSummaryStep()}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Helper: resolve the correct schema given offering state
// ---------------------------------------------------------------------------

const resolveSchema = (
  serviceType: ServiceType,
  hasPaymentPlan: boolean,
): AgreementSchema => {
  if (serviceType === ServiceType.CREMACIONES) return createCremacionesAgreementSchema()
  if (serviceType === ServiceType.ABONOS) return createAbonosAgreementSchema(hasPaymentPlan)
  return createBaseAgreementSchema(serviceType)
}

// ---------------------------------------------------------------------------
// Outer shell – handles offering/client selection, then mounts the inner form
// ---------------------------------------------------------------------------

interface CreateAgreementCmpProps {
  /** Empty props */
}

export const CreateAgreementCmp = ({ }: CreateAgreementCmpProps) => {
  const { data: offerings } = useOfferings()
  const navigate = useNavigate()

  // Temporary selections while the user has not yet picked an offering
  const [pendingUserId, setPendingUserId] = useState('')
  const [pendingOfferingId, setPendingOfferingId] = useState('')
  const [pendingServiceType, setPendingServiceType] = useState<ServiceType | undefined>(undefined)

  const selectedOffering = offerings?.find((o) => o.id === pendingOfferingId)
  const serviceType = pendingServiceType || selectedOffering?.serviceType
  const hasPaymentPlan = selectedOffering?.hasPaymentPlanAvailable ?? false

  // Only derive the schema once we have a confirmed offering
  const schema = selectedOffering
    ? resolveSchema(serviceType!, hasPaymentPlan)
    : null

  // PARCELAS: redirect immediately to the dedicated section
  if (serviceType === ServiceType.PARCELAS) {
    return (
      <div className="p-6 border border-amber-200 bg-amber-50 rounded-xl space-y-4 animate-in fade-in slide-in-from-top-1">
        <div className="flex flex-col gap-1">
          <h4 className="font-semibold text-amber-900 text-lg">Venta de parcelas</h4>
          <p className="text-sm text-amber-800">
            Para realizar una venta de una parcela, debe hacerlo desde la sección de Gestión de Parcelas.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => navigate({ to: `${Paths.Management.Contracts}` })}>
            Cancelar
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => navigate({ to: `${Paths.Management.Parcels}` })}
          >
            Ir a Gestión de Parcelas
          </Button>
        </div>
      </div>
    )
  }

  // While waiting for the user to choose client + offering, render a minimal
  // selector so we can determine which schema to use.
  if (!schema || !selectedOffering) {
    return (
      <OfferingSelector
        pendingUserId={pendingUserId}
        pendingOfferingId={pendingOfferingId}
        pendingServiceType={pendingServiceType}
        onUserIdChange={setPendingUserId}
        onOfferingIdChange={setPendingOfferingId}
        onServiceTypeChange={setPendingServiceType}
      />
    )
  }

  // Mount AgreementFormInner with a key that changes whenever the
  // offering or hasPaymentPlan flag changes → guarantees a fresh useForm.
  const formKey = `${selectedOffering.id}__${hasPaymentPlan}`

  return (
    <AgreementFormInner
      key={formKey}
      offering={selectedOffering}
      schema={schema}
      initialUserId={pendingUserId}
      onUserIdChange={setPendingUserId}
      onOfferingIdChange={setPendingOfferingId}
      onServiceTypeChange={setPendingServiceType}
    />
  )
}

// ---------------------------------------------------------------------------
// Minimal offering-selector shown before the main form mounts
// ---------------------------------------------------------------------------

interface OfferingSelectorProps {
  pendingUserId: string
  pendingOfferingId: string
  pendingServiceType?: ServiceType
  onUserIdChange: (id: string) => void
  onOfferingIdChange: (id: string) => void
  onServiceTypeChange: (type: ServiceType | undefined) => void
}

/**
 * A lightweight stub form used solely to capture userId + offeringId before
 * we know which Zod schema to use.  Once both are set, the parent replaces
 * this component with the fully-typed AgreementFormInner.
 */
const OfferingSelector = ({
  pendingUserId,
  pendingOfferingId,
  pendingServiceType,
  onUserIdChange,
  onOfferingIdChange,
  onServiceTypeChange,
}: OfferingSelectorProps) => {
  const navigate = useNavigate()

  // A permissive stub form just to drive ClientServiceSelector's controlled inputs
  const form = useForm<CreateAgreementData>({
    defaultValues: {
      userId: pendingUserId,
      offeringId: pendingOfferingId,
      serviceType: pendingServiceType,
      startDate: new Date(),
      amount: 0,
    },
  })

  // Propagate changes upward so the parent can react
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.userId !== undefined) onUserIdChange(values.userId)
      if (values.offeringId !== undefined) onOfferingIdChange(values.offeringId)
      if (values.serviceType !== undefined) onServiceTypeChange(values.serviceType)
    })
    return () => subscription.unsubscribe()
  }, [form, onUserIdChange, onOfferingIdChange, onServiceTypeChange])

  return (
    <div className="space-y-6">
      <StepHeader title="Seleccione el cliente y servicio a prestar" description="">
        <ClientServiceSelector form={form} />
      </StepHeader>

      <div className="flex justify-between space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={() => navigate({ to: `${Paths.Management.Contracts}` })}>
          Cancelar
        </Button>
        <Button type="button" disabled>
          Siguiente
        </Button>
      </div>
    </div>
  )
}
